import { CircleCheckBig, CircleX } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { DisabledReasonTooltip } from '@/components/ui/disabled-reason-tooltip';
import { ExecutionInProgressButton } from '@/components/execution/execution-in-progress-button';

interface ProposalActionButtonsProps {
  // Review actions (create signatures)
  onReject: () => void;
  onApprove: () => void;
  // Execute actions (finalize proposal)
  onExecuteRejection: () => void;
  onExecute: () => void;
  // Current user's vote status
  hasApproved: boolean;
  hasRejected: boolean;
  // Threshold status
  canExecuteRejection: boolean; // numRejections >= requiredApprovals
  canExecute: boolean; // numApprovals >= requiredApprovals
  // Loading/disabled state
  disabled?: boolean;
  approveDisabled?: boolean;
  approveDisabledReason?: string | undefined;
  isExecuting?: boolean;
  /** When set, appends to review/execute action labels (e.g. "Cancellation" → "Reject Cancellation"). */
  proposalName?: string;
}

/**
 * Proposal action buttons that adapt based on approval/rejection thresholds.
 *
 * Button States:
 * - Left: "Reject" (review) OR "Execute Rejection" (when enough rejections)
 * - Right: "Approve" (review) OR "Execute" (when enough approvals)
 *
 * This creates 4 possible combinations:
 * 1. REJECT | APPROVE - Neither threshold met
 * 2. EXECUTE REJECTION | APPROVE - Enough rejections, not enough approvals
 * 3. REJECT | EXECUTE - Enough approvals, not enough rejections
 * 4. EXECUTE REJECTION | EXECUTE - Both thresholds met
 */
function getRejectLabel(
  canExecuteRejection: boolean,
  hasRejected: boolean,
  proposalName: string | undefined
): string {
  if (canExecuteRejection) {
    return proposalName
      ? `Execute rejection ${proposalName}`
      : 'Execute Rejection';
  }
  if (hasRejected) {
    return 'You Rejected';
  }
  return proposalName ? `Reject ${proposalName}` : 'Reject';
}

function getApproveLabel(
  canExecute: boolean,
  hasApproved: boolean,
  proposalName: string | undefined
): string {
  if (canExecute) {
    return proposalName ? `Execute ${proposalName}` : 'Execute';
  }
  if (hasApproved) {
    return 'You Approved';
  }
  return proposalName ? `Approve ${proposalName}` : 'Approve';
}

/**
 * Renders proposal actions, replacing them with execution progress while the
 * target is active. Callers must provide execution state for that proposal.
 */
export function ProposalActionButtons({
  onReject,
  onApprove,
  onExecuteRejection,
  onExecute,
  hasApproved,
  hasRejected,
  canExecuteRejection,
  canExecute,
  disabled = false,
  approveDisabled = false,
  approveDisabledReason,
  isExecuting = false,
  proposalName,
}: ProposalActionButtonsProps) {
  // Case: active execution replaces every review and execution action.
  if (isExecuting) {
    return (
      <ExecutionInProgressButton
        className="h-[70px] min-w-[300px] text-lg font-semibold"
        size="lg"
      />
    );
  }

  const isApproveDisabled =
    disabled || approveDisabled || (!canExecute && hasApproved);
  const approveButton = (
    <Button
      size="lg"
      className="w-fit min-w-[300px] pr-[5px] pl-[25px] bg-green-700 text-yellow-100 text-shadow-sm border-lime-300 hover:bg-green-600 h-[70px] text-lg font-semibold"
      onClick={canExecute ? onExecute : onApprove}
      disabled={isApproveDisabled}
    >
      <span className="relative inline-flex items-center justify-center">
        <CircleCheckBig
          size={15}
          strokeWidth={2}
          className="absolute -left-6 top-1/2 -translate-y-1/2"
        />
        {getApproveLabel(canExecute, hasApproved, proposalName)}
      </span>
    </Button>
  );

  return (
    <div className="flex gap-3">
      {/* Left Button: Reject or Execute Rejection */}
      <Button
        size="lg"
        className="w-fit min-w-[300px] pr-[5px] pl-[25px] bg-rose-700 text-orange-100 text-shadow-sm border-orange-300 hover:bg-red-600 h-[70px] text-lg font-semibold"
        onClick={canExecuteRejection ? onExecuteRejection : onReject}
        disabled={disabled || (!canExecuteRejection && hasRejected)}
      >
        <span className="relative inline-flex items-center justify-center">
          <CircleX
            size={15}
            strokeWidth={2}
            className="absolute -left-6 top-1/2 -translate-y-1/2"
          />
          {getRejectLabel(canExecuteRejection, hasRejected, proposalName)}
        </span>
      </Button>

      {/* Right Button: Approve or Execute */}
      <DisabledReasonTooltip reason={approveDisabledReason}>
        {approveButton}
      </DisabledReasonTooltip>
    </div>
  );
}
